import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Plus, ChevronRight, Loader2, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { SubjectPill } from "@/lib/accents";

export default function Courses() {
  const qc = useQueryClient();
  const [course, setCourse] = useState(null);
  const [module, setModule] = useState(null);
  const [topic, setTopic] = useState(null);
  const [dialog, setDialog] = useState(null); // {type}
  const [del, setDel] = useState(null); // {type, item}
  const [edit, setEdit] = useState(null); // course being renamed/edited
  const [rename, setRename] = useState(null); // {type, item} for module/topic/deck

  const { data: coursesData, isLoading } = useQuery({ queryKey: ["admin-courses"], queryFn: async () => (await api.get("/admin/courses")).data });
  const { data: modulesData } = useQuery({ queryKey: ["admin-modules", course?.id], enabled: !!course, queryFn: async () => (await api.get(`/admin/modules?course_id=${course.id}`)).data });
  const { data: topicsData } = useQuery({ queryKey: ["admin-topics", module?.id], enabled: !!module, queryFn: async () => (await api.get(`/admin/topics?module_id=${module.id}`)).data });
  const { data: decksData } = useQuery({ queryKey: ["admin-decks", topic?.id], enabled: !!topic, queryFn: async () => (await api.get(`/admin/decks?topic_id=${topic.id}`)).data });

  const invalidate = (key) => qc.invalidateQueries({ queryKey: [key] });

  const Column = ({ title, items, selected, onSelect, onAdd, testid, subtitle, onDelete, onEdit }) => (
    <div className="flex-1 min-w-[200px] rounded-xl border border-slate-200 bg-white flex flex-col">
      <div className="p-3 border-b border-slate-100 flex items-center justify-between">
        <div>
          <div className="font-display font-semibold text-slate-900 text-sm">{title}</div>
          {subtitle && <div className="text-xs text-slate-400">{subtitle}</div>}
        </div>
        {onAdd && <Button size="sm" variant="ghost" onClick={onAdd} data-testid={`add-${testid}`}><Plus className="h-4 w-4" /></Button>}
      </div>
      <div className="p-2 space-y-1 overflow-auto max-h-[60vh]">
        {items?.length === 0 && <p className="text-xs text-slate-400 p-2">Nothing yet.</p>}
        {items?.map((it) => (
          <div key={it.id}
            className={`flex items-stretch rounded-lg transition-colors ${selected?.id === it.id ? "bg-slate-900 text-white" : "hover:bg-slate-50 text-slate-700"}`}>
            <button onClick={() => onSelect(it)} data-testid={`${testid}-item-${it.id}`}
              className="flex-1 min-w-0 flex items-center justify-between px-3 py-2 text-left text-sm">
              <span className="truncate">{it.name}</span>
              <ChevronRight className="h-4 w-4 opacity-50 shrink-0 ml-2" />
            </button>
            {onEdit && (
              <button onClick={(e) => { e.stopPropagation(); onEdit(it); }} data-testid={`edit-${testid}-${it.id}`} title={`Rename ${title.slice(0, -1).toLowerCase()}`}
                className={`shrink-0 px-2 flex items-center ${selected?.id === it.id ? "text-white/70 hover:text-white" : "text-slate-400 hover:text-slate-700 hover:bg-slate-100"}`}>
                <Pencil className="h-3.5 w-3.5" />
              </button>
            )}
            {onDelete && (
              <button onClick={(e) => { e.stopPropagation(); onDelete(it); }} data-testid={`delete-${testid}-${it.id}`} title={`Delete ${title.slice(0, -1).toLowerCase()}`}
                className={`shrink-0 px-2 flex items-center rounded-r-lg ${selected?.id === it.id ? "text-white/70 hover:text-white" : "text-rose-400 hover:text-rose-600 hover:bg-rose-50"}`}>
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  if (isLoading) return <div className="py-20 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-6">
      <div>
        <p className="font-mono text-xs uppercase tracking-widest text-slate-400">Admin</p>
        <h1 className="mt-1 font-display text-3xl font-bold text-slate-900">Courses</h1>
        <p className="mt-1 text-slate-500">Course → Module → Topic → Deck. New subjects and exam boards can be added freely.</p>
      </div>

      <div className="flex gap-3 overflow-x-auto pb-2">
        <Column title="Courses" testid="course" items={coursesData?.courses}
          selected={course} onSelect={(c) => { setCourse(c); setModule(null); setTopic(null); }}
          onAdd={() => setDialog({ type: "course" })} onDelete={(c) => setDel({ type: "course", item: c })}
          onEdit={(c) => setEdit(c)} />
        {course && <Column title="Modules" subtitle={course.name} testid="module" items={modulesData?.modules}
          selected={module} onSelect={(m) => { setModule(m); setTopic(null); }} onAdd={() => setDialog({ type: "module" })}
          onDelete={(m) => setDel({ type: "module", item: m })} onEdit={(m) => setRename({ type: "module", item: m })} />}
        {module && <Column title="Topics" subtitle={module.name} testid="topic" items={topicsData?.topics}
          selected={topic} onSelect={setTopic} onAdd={() => setDialog({ type: "topic" })}
          onDelete={(t) => setDel({ type: "topic", item: t })} onEdit={(t) => setRename({ type: "topic", item: t })} />}
        {topic && <Column title="Decks" subtitle={topic.name} testid="deck" items={decksData?.decks}
          selected={null} onSelect={() => {}} onAdd={() => setDialog({ type: "deck" })}
          onDelete={(d) => setDel({ type: "deck", item: d })} onEdit={(d) => setRename({ type: "deck", item: d })} />}
      </div>

      <CreateDialog dialog={dialog} onClose={() => setDialog(null)}
        onDone={(type) => { setDialog(null); invalidate(type === "course" ? "admin-courses" : type === "module" ? "admin-modules" : type === "topic" ? "admin-topics" : "admin-decks"); }}
        context={{ course, module, topic }} />

      <DeleteHierarchyDialog del={del} onClose={() => setDel(null)}
        onDone={(type, item) => {
          setDel(null);
          invalidate(type === "course" ? "admin-courses" : type === "module" ? "admin-modules" : type === "topic" ? "admin-topics" : "admin-decks");
          if (type === "course" && course?.id === item.id) { setCourse(null); setModule(null); setTopic(null); }
          if (type === "module" && module?.id === item.id) { setModule(null); setTopic(null); }
          if (type === "topic" && topic?.id === item.id) { setTopic(null); }
        }} />

      <EditCourseDialog course={edit} onClose={() => setEdit(null)}
        onSaved={(saved) => { setEdit(null); invalidate("admin-courses"); if (course?.id === saved.id) setCourse(saved); }} />

      <RenameDialog rename={rename} onClose={() => setRename(null)}
        onSaved={(type, item) => {
          setRename(null);
          invalidate(type === "module" ? "admin-modules" : type === "topic" ? "admin-topics" : "admin-decks");
          if (type === "module" && module?.id === item.id) setModule(item);
          if (type === "topic" && topic?.id === item.id) setTopic(item);
        }} />
    </div>
  );
}

function RenameDialog({ rename, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const type = rename?.type;
  useEffect(() => { if (rename) setName(rename.item.name || ""); }, [rename]);
  const save = async () => {
    setBusy(true);
    try {
      const coll = { module: "modules", topic: "topics", deck: "decks" }[type];
      await api.patch(`/admin/${coll}/${rename.item.id}`, { name });
      toast.success("Renamed");
      onSaved(type, { ...rename.item, name });
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!rename} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="rename-dialog">
        <DialogHeader><DialogTitle className="capitalize">Rename {type}</DialogTitle>
          <DialogDescription>Update the name. Cards and student progress are kept.</DialogDescription>
        </DialogHeader>
        <div><Label>Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="rename-input" className="mt-1.5" /></div>
        <DialogFooter><Button onClick={save} disabled={busy || !name.trim()} data-testid="save-rename" className="bg-slate-900 hover:bg-slate-800">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditCourseDialog({ course, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (course) setForm({
      name: course.name || "", exam_board: course.exam_board || "",
      qualification: course.qualification || "", subject: course.subject || "",
      description: course.description || "", academic_year: course.academic_year || "",
      icon: course.icon || "", display_order: course.display_order ?? 0,
    });
  }, [course]);
  const submit = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/courses/${course.id}`, form);
      toast.success("Course updated");
      onSaved({ ...course, ...form });
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!course} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="edit-course-dialog">
        <DialogHeader><DialogTitle>Edit course</DialogTitle>
          <DialogDescription>Rename the course or update its details. Student enrolments and progress are kept.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>Name</Label><Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="edit-course-name" className="mt-1.5" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Exam board</Label><Input value={form.exam_board || ""} onChange={(e) => setForm({ ...form, exam_board: e.target.value })} className="mt-1.5" placeholder="OCR A" /></div>
            <div><Label>Qualification</Label><Input value={form.qualification || ""} onChange={(e) => setForm({ ...form, qualification: e.target.value })} className="mt-1.5" placeholder="A-Level" /></div>
          </div>
          <div><Label>Subject</Label><Input value={form.subject || ""} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="mt-1.5" placeholder="Biology" /></div>
        </div>
        <DialogFooter><Button onClick={submit} disabled={busy || !form.name} data-testid="save-course-edit" className="bg-slate-900 hover:bg-slate-800">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteHierarchyDialog({ del, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const type = del?.type;
  const scope = { course: "its modules, topics, decks and cards", module: "its topics, decks and cards", topic: "its decks and cards", deck: "its cards" }[type];
  const remove = async () => {
    setBusy(true);
    try {
      const coll = { course: "courses", module: "modules", topic: "topics", deck: "decks" }[type];
      const { data } = await api.delete(`/admin/${coll}/${del.item.id}`);
      toast.success(`${type[0].toUpperCase()}${type.slice(1)} deleted${data.cards_deleted ? ` (${data.cards_deleted} cards removed)` : ""}`);
      onDone(type, del.item);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!del} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="delete-hierarchy-dialog">
        <DialogHeader><DialogTitle className="capitalize">Delete {type}</DialogTitle>
          <DialogDescription>
            This permanently deletes <b>{del?.item?.name}</b> and {scope}, along with all student progress for those cards. This can’t be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="cancel-delete-hierarchy">Cancel</Button>
          <Button onClick={remove} disabled={busy} data-testid="confirm-delete-hierarchy" className="bg-rose-600 hover:bg-rose-700 text-white">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Delete permanently"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreateDialog({ dialog, onClose, onDone, context }) {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const type = dialog?.type;

  const submit = async () => {
    setBusy(true);
    try {
      if (type === "course") {
        await api.post("/admin/courses", {
          name: form.name, exam_board: form.exam_board || "", qualification: form.qualification || "",
          subject: form.subject || "", description: form.description || "",
        });
      } else if (type === "module") {
        await api.post("/admin/modules", { course_id: context.course.id, name: form.name });
      } else if (type === "topic") {
        await api.post("/admin/topics", { course_id: context.course.id, module_id: context.module.id, name: form.name });
      } else if (type === "deck") {
        await api.post("/admin/decks", { course_id: context.course.id, module_id: context.module.id, topic_id: context.topic.id, name: form.name });
      }
      toast.success("Created");
      setForm({});
      onDone(type);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!dialog} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="create-hierarchy-dialog">
        <DialogHeader><DialogTitle className="capitalize">New {type}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Name</Label><Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="hierarchy-name-input" className="mt-1.5" /></div>
          {type === "course" && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Exam board</Label><Input value={form.exam_board || ""} onChange={(e) => setForm({ ...form, exam_board: e.target.value })} className="mt-1.5" placeholder="OCR A" /></div>
                <div><Label>Qualification</Label><Input value={form.qualification || ""} onChange={(e) => setForm({ ...form, qualification: e.target.value })} className="mt-1.5" placeholder="A-Level" /></div>
              </div>
              <div><Label>Subject</Label><Input value={form.subject || ""} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="mt-1.5" placeholder="Biology" /></div>
            </>
          )}
        </div>
        <DialogFooter><Button onClick={submit} disabled={busy || !form.name} data-testid="create-hierarchy-submit" className="bg-slate-900 hover:bg-slate-800">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
